import React, {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {restRequest} from '../../../api-utils/apiQueries'
import {convertToHours} from '../../common-function/formatDateToYearMonth'
import {
  formatPreferredContactTime,
  getPreferredContactTime,
} from './feedbackContactTime'

type Props = {
  showDetails: boolean
  toggle: () => void
  selectedId: number
}

const DetailField = ({label, value}: {label: string; value: React.ReactNode}) => (
  <div className='mb-3'>
    <div className='text-muted fw-semibold fs-7 mb-1'>{label}</div>
    <div className='fw-semibold text-gray-900 text-break'>{value}</div>
  </div>
)

const getRatingMeta = (value: unknown) => {
  const rating = Number(value)
  if (!Number.isFinite(rating) || rating <= 0) {
    return {
      label: '—',
      color: 'secondary' as const,
      note: 'Not rated',
    }
  }
  if (rating >= 4) {
    return {
      label: `${rating}/5`,
      color: 'success' as const,
      note: 'Positive',
    }
  }
  if (rating >= 3) {
    return {
      label: `${rating}/5`,
      color: 'warning' as const,
      note: 'Neutral',
    }
  }
  return {
    label: `${rating}/5`,
    color: 'danger' as const,
    note: 'Needs attention',
  }
}

const RatingBadge = ({value}: {value: unknown}) => {
  const meta = getRatingMeta(value)
  return (
    <span className={`badge badge-light-${meta.color} fw-bold text-nowrap`}>
      {meta.label}
    </span>
  )
}

const FeedbackDetails = ({showDetails, toggle, selectedId}: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [feedback, setFeedback] = useState<any>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!showDetails || !selectedId) return

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setFeedback(null)
      setError('')
      try {
        const response: any = await restRequest.getFeedbackDetailData(selectedId)
        if (!cancelled) {
          setFeedback(response?.data?.data ?? null)
          if (!response?.data?.data) setError('Feedback not found.')
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message || err?.message || 'Unable to load feedback details.'
          )
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [selectedId, showDetails])

  const feedbackRates = [
    {name: 'Mates', rating: feedback?.mate_rate, comments: feedback?.mate_feedback},
    {name: 'Rides', rating: feedback?.ride_rate, comments: feedback?.ride_feedback},
    {name: 'Wallet', rating: feedback?.wallet_rate, comments: feedback?.wallet_feedback},
    {name: 'Rewards', rating: feedback?.reward_rate, comments: feedback?.reward_feedback},
    {
      name: 'Live trip tracking',
      rating: feedback?.live_tracking_rate,
      comments: feedback?.live_tracking_feedback,
    },
  ]

  const commentsData = [
    {name: 'Bugs and technical issues', comments: feedback?.bug_and_technical_issue},
    {name: 'Suggestions for improvement', comments: feedback?.suggestions_for_improvement},
    {name: 'Safety and security', comments: feedback?.safety_and_security},
    {name: 'Communication tools', comments: feedback?.communication_tools},
    {name: 'Payment and pricing', comments: feedback?.payment_and_pricing},
    {name: 'User profiles and preferences', comments: feedback?.user_profiles_and_preferences},
    {name: 'Privacy and data handling', comments: feedback?.privacy_and_data_handling},
  ]

  const submittedLabel = feedback?.createdAt
    ? `${new Date(feedback.createdAt).toLocaleDateString()} · ${convertToHours(new Date(feedback.createdAt))}`
    : '—'

  const userInitial = String(feedback?.user?.name ?? '?').charAt(0).toUpperCase()

  const visibleComments = commentsData.filter((item) => String(item.comments ?? '').trim())

  return (
    <Modal show={showDetails} className='modal-lg' onHide={toggle}>
      <Modal.Header className='bg-primary-subtle' closeButton>
        <Modal.Title>Feedback details</Modal.Title>
      </Modal.Header>
      <Modal.Body className='p-6'>
        {isLoading ? (
          <div className='d-flex justify-content-center py-10'>
            <div className='spinner-border text-primary' role='status' />
          </div>
        ) : error || !feedback ? (
          <div className='alert alert-danger mb-0'>{error || 'Feedback not found.'}</div>
        ) : (
          <>
            <div className='rounded border border-gray-200 bg-light-primary bg-opacity-50 p-4 mb-5'>
              <div className='d-flex flex-column flex-md-row justify-content-between gap-4'>
                <div className='d-flex align-items-center gap-4'>
                  <div className='symbol symbol-48px flex-shrink-0'>
                    <div className='symbol-label bg-white text-primary fs-3 fw-bold'>
                      {userInitial}
                    </div>
                  </div>
                  <div>
                    <div className='text-muted fw-semibold fs-7 mb-1'>Submitted by</div>
                    {feedback.user?.id ? (
                      <>
                        <Link
                          to={`/user-management/${feedback.user.id}`}
                          className='fs-5 fw-bold text-gray-900 text-hover-primary'
                        >
                          {feedback.user.name || 'View profile'}
                        </Link>
                        <div className='text-muted fs-6 mt-1'>{feedback.user.email ?? '—'}</div>
                      </>
                    ) : (
                      <>
                        <div className='fs-5 fw-bold text-gray-900'>{feedback.user?.name ?? '—'}</div>
                        <div className='text-muted fs-6 mt-1'>{feedback.user?.email ?? '—'}</div>
                      </>
                    )}
                  </div>
                </div>
                <div className='d-flex flex-wrap align-items-center align-content-start gap-2'>
                  <span className='badge badge-light fw-semibold py-2 px-3'>
                    {submittedLabel}
                  </span>
                  <span
                    className={`badge ${
                      feedback.can_we_connect ? 'badge-light-success' : 'badge-light-secondary'
                    } fw-semibold py-2 px-3`}
                  >
                    {feedback.can_we_connect ? 'Can contact' : 'No contact requested'}
                  </span>
                </div>
              </div>
            </div>

            <div className='row g-5 mb-5'>
              <div className='col-md-6'>
                <div className='rounded border border-gray-200 p-4 h-100'>
                  <div className='d-flex align-items-center justify-content-between gap-3 mb-3'>
                    <span className='fw-bold text-gray-900'>Overall experience</span>
                    <RatingBadge value={feedback.overall_experience} />
                  </div>
                  <div className='d-flex align-items-center justify-content-between gap-3'>
                    <span className='fw-bold text-gray-900'>Overall satisfaction</span>
                    <RatingBadge value={feedback.overall_satisfaction} />
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='rounded border border-gray-200 p-4 h-100'>
                  <div className='row g-3'>
                    <div className='col-sm-6'>
                      <DetailField label='Mobile' value={feedback.user?.mobile ?? '—'} />
                    </div>
                    <div className='col-sm-6'>
                      <DetailField
                        label='Preferred contact'
                        value={
                          feedback.can_we_connect
                            ? formatPreferredContactTime(getPreferredContactTime(feedback))
                            : '—'
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='rounded border border-gray-200 mb-5'>
              <div className='px-4 py-3 border-bottom'>
                <h5 className='fw-bold text-gray-900 mb-0'>Feature ratings</h5>
              </div>
              <div className='table-responsive'>
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-2 mb-0'>
                  <tbody>
                    {feedbackRates.map((item) => (
                      <tr key={item.name}>
                        <td className='ps-4 min-w-160px fw-semibold text-gray-900'>{item.name}</td>
                        <td className='min-w-90px'>
                          <RatingBadge value={item.rating} />
                        </td>
                        <td className='pe-4 text-gray-700 text-break'>
                          {item.comments || <span className='text-muted'>No comment</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className='rounded border border-gray-200'>
              <div className='px-4 py-3 border-bottom'>
                <h5 className='fw-bold text-gray-900 mb-0'>Open feedback</h5>
              </div>
              <div className='p-4'>
                {visibleComments.length > 0 ? (
                  <div className='d-flex flex-column gap-4'>
                    {visibleComments.map((item) => (
                      <div key={item.name}>
                        <div className='fw-bold text-gray-900 mb-1'>{item.name}</div>
                        <div className='text-gray-700 text-break'>{item.comments}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className='text-muted fst-italic'>No open comments provided.</div>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={toggle}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default FeedbackDetails
